// Pure grid auto-detection. No DOM, no state imports.

import type { Rect } from '~/types/pipeline';

export function uniformDividers(start: number, length: number, count: number): number[] {
  const out: number[] = [];
  for (let i = 0; i <= count; i++) out.push(Math.round(start + (length * i) / count));
  return out;
}

export type GridDetection = {
  rows: number;
  cols: number;
  confidence: number;
};

/**
 * Detect the likely number of rows and columns in a sprite sheet by
 * analysing alpha histograms along each axis.
 *
 * Algorithm:
 *   1. Compute column-alpha sums (sum of alpha over all rows, per x).
 *   2. Compute row-alpha sums (sum of alpha over all cols, per y).
 *   3. Detect "gap" runs — contiguous columns/rows where the alpha sum
 *      falls below a threshold (10% of the maximum observed sum).
 *   4. Count gap runs → number of cells on that axis.
 *   5. Measure spacing between gap center points; confidence =
 *      1 - stdev(spacings) / mean(spacings), clamped to [0, 1].
 *      Uniform gaps → confidence ≈ 1; chaotic → confidence ≈ 0.
 *
 * Returns rows=1, cols=1, confidence=0 when no clear gaps are found.
 */
export function autoDetectGrid(image: ImageData, bounds: Rect): GridDetection {
  const { data, width } = image;
  const { x: bx, y: by, w: bw, h: bh } = bounds;

  // --- column alpha sums ---
  const colSums = new Float64Array(bw);
  for (let row = 0; row < bh; row++) {
    for (let col = 0; col < bw; col++) {
      const idx = ((by + row) * width + (bx + col)) * 4;
      colSums[col] = (colSums[col] ?? 0) + (data[idx + 3] ?? 0);
    }
  }

  // --- row alpha sums ---
  const rowSums = new Float64Array(bh);
  for (let row = 0; row < bh; row++) {
    for (let col = 0; col < bw; col++) {
      const idx = ((by + row) * width + (bx + col)) * 4;
      rowSums[row] = (rowSums[row] ?? 0) + (data[idx + 3] ?? 0);
    }
  }

  const { count: cols, confidence: colConf } = detectGaps(colSums);
  const { count: rows, confidence: rowConf } = detectGaps(rowSums);

  // Combined confidence: if at least one axis shows regularity, report that.
  // Using the max rather than the average avoids penalising a sprite sheet
  // that has columns but uniform (gap-free) rows.
  const confidence = Math.max(0, Math.min(1, Math.max(colConf, rowConf)));

  return { rows: Math.max(1, rows), cols: Math.max(1, cols), confidence };
}

/**
 * Given an array of alpha sums per slice, detect gap runs (where alpha sum
 * is below threshold) and return:
 * - count: number of gap runs (= number of cells detected on this axis)
 * - confidence: regularity of gap spacing (0 = random, 1 = perfectly uniform)
 */
function detectGaps(sums: Float64Array): { count: number; confidence: number } {
  let maxSum = 0;
  for (const v of sums) if (v > maxSum) maxSum = v;
  if (maxSum === 0) return { count: 1, confidence: 0 };

  const threshold = maxSum * 0.1;

  // Collect gap run center positions
  const gapCenters: number[] = [];
  let inGap = false;
  let gapStart = 0;

  for (let i = 0; i < sums.length; i++) {
    const isGap = (sums[i] ?? 0) < threshold;
    if (isGap && !inGap) {
      inGap = true;
      gapStart = i;
    } else if (!isGap && inGap) {
      inGap = false;
      gapCenters.push((gapStart + i - 1) / 2);
    }
  }
  // Close a gap that runs to the end
  if (inGap) {
    gapCenters.push((gapStart + sums.length - 1) / 2);
  }

  if (gapCenters.length === 0) {
    return { count: 1, confidence: 0 };
  }

  // Number of cells = number of gaps (each gap separates two cells,
  // and the last gap may trail off the edge)
  const count = gapCenters.length;

  if (gapCenters.length < 2) {
    // Only one gap → one cell found but we can't compute spacing regularity
    return { count, confidence: 0.5 };
  }

  // Spacings between consecutive gap centers
  const spacings: number[] = [];
  for (let i = 1; i < gapCenters.length; i++) {
    spacings.push((gapCenters[i] ?? 0) - (gapCenters[i - 1] ?? 0));
  }

  const mean = spacings.reduce((a, b) => a + b, 0) / spacings.length;
  if (mean === 0) return { count, confidence: 0 };

  const variance = spacings.reduce((a, b) => a + (b - mean) ** 2, 0) / spacings.length;
  const stdev = Math.sqrt(variance);

  const confidence = Math.max(0, Math.min(1, 1 - stdev / mean));
  return { count, confidence };
}
